import React, { useCallback, useMemo, useState } from 'react';
import FluidButton from 'ui/FluidButton';
import { AnonymousAvatarComponent as AnonymousAvatarIcon } from 'ui/Icons';
import { theme } from '../../../../tailwind.config';
import { usePostBankTransactionComment } from '../hooks/useCommentQuery';
import Editor, { IMentionOption } from 'pureUi/Editor';
import { useBackendUsers } from 'services/BackendApi/queries/useBackendUsers';

interface IBankTransactionCommentsEditProps {
  onCancel: () => void;
  transactionUuid: string;
  showSuccessNotification: (message: string) => void;
  showErrorNotification: (message: string) => void;
}

export const BankTransactionCommentsEdit: React.FC<IBankTransactionCommentsEditProps> = ({ transactionUuid, onCancel, showSuccessNotification, showErrorNotification }) => {
  const [comment, setComment] = useState('');
  const { mutate: postComment, isPending: isSaving } = usePostBankTransactionComment(showSuccessNotification, showErrorNotification);
  const beUsers = useBackendUsers('', { sort: 'firstName' });
  const isCommentEmpty = comment.trim().length === 0;

  const mentionOptions: IMentionOption[] = useMemo(() => beUsers?.data?.data?.data?.map(user => ({
    name: `${user.firstName} ${user.lastName}`,
    email: user.email,
    role: user.type,
    id: user.uuid,
  })) ?? [], [beUsers]);

  const handleCancelEdit = useCallback(() => {
    setComment('');
    onCancel();
  }, [onCancel]);

  const handleConfirmEdit = useCallback(() => {
    if (!isCommentEmpty) {
      postComment(
        { transactionUuid, commentText: comment.trim() },
        {
          onSuccess: () => {
            setComment('');
            onCancel();
          },
        }
      );
    }
  }, [postComment, comment, transactionUuid, onCancel, isCommentEmpty]);

  const handleCommentChange = useCallback((content: string, editor: any) => {
    setComment(content);
  }, []);

  return (
    <div className="edit-comment flex w-full items-start gap-[10px]">
      <div className="user-picture">
        <AnonymousAvatarIcon icon={theme.colors['gray-100']} fill={theme.colors['gray-40']} />
      </div>
      <div className="comment-container flex-1 relative">
      <Editor
          text={comment}
          handleEditorChange={handleCommentChange}
          options={mentionOptions}
          placeHolder="Type @ to mention and notify someone."
        />
        <div className="buttons flex gap-[10px] mt-[10px]">
          <FluidButton type="secondary" onClick={handleCancelEdit} disabled={isSaving}>
            Cancel
          </FluidButton>
          <FluidButton
            type="primary"
            onClick={handleConfirmEdit}
            isLoading={isSaving}
            disabled={isSaving || isCommentEmpty}
          >
            Save
          </FluidButton>
        </div>
      </div>
    </div>
  );
};

